import { cloneElement, ComponentProps, ReactElement, ReactNode } from 'react';
import { Dropdown as PrimeDropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import styles from './Dropdown.module.scss';

type DropdownValue = string | number;
type DropdownSelectionValue = DropdownValue | DropdownValue[] | null | undefined;

type MultiSelectPanelHeaderTemplateOptions = {
  checkboxElement: ReactElement;
  checked: boolean;
  className: string;
  closeElement: ReactNode;
  filterElement: ReactNode;
  onChange: (event: PanelHeaderCheckboxChangeEvent) => void;
  props: {
    filterValue: string;
  };
};

type PanelHeaderCheckboxChangeEvent = {
  originalEvent: Event;
};

const isDropdownValue = (value: unknown): value is DropdownValue =>
  typeof value === 'string' || typeof value === 'number';

const toDropdownValues = (value: DropdownSelectionValue): DropdownValue[] =>
  Array.isArray(value) ? [...value] : [];

const getOptionLabel = (option: Partial<SelectItem> | string): string =>
  typeof option === 'string' ? option : option.label || '';

const getOptionValue = (option: Partial<SelectItem> | string): DropdownValue | undefined => {
  if (typeof option === 'string') {
    return option;
  }

  return option.value;
};

const normalizeSearchValue = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .trim();

const getVisibleOptionValues = (
  options: Array<Partial<SelectItem> | string>,
  filterValue: string
): DropdownValue[] => {
  const normalizedFilterValue = normalizeSearchValue(filterValue);

  return options
    .filter(
      (option) =>
        normalizedFilterValue === '' ||
        normalizeSearchValue(getOptionLabel(option)).includes(normalizedFilterValue)
    )
    .map(getOptionValue)
    .filter(isDropdownValue);
};

const getToggledSelection = (
  selectedValues: DropdownValue[],
  visibleValues: DropdownValue[]
): { allVisibleValuesSelected: boolean; nextSelectedValues: DropdownValue[] } => {
  const selectedValueSet = new Set(selectedValues);
  const allVisibleValuesSelected =
    visibleValues.length > 0 &&
    visibleValues.every((visibleValue) => selectedValueSet.has(visibleValue));
  const visibleValueSet = new Set(visibleValues);
  const nextSelectedValues = allVisibleValuesSelected
    ? selectedValues.filter((selectedValue) => !visibleValueSet.has(selectedValue))
    : Array.from(new Set([...selectedValues, ...visibleValues]));

  return { allVisibleValuesSelected, nextSelectedValues };
};

export enum DropdownSizes {
  small = 'p-dropdown-sm',
  medium = 'p-dropdown'
}

const Dropdown = ({
  label,
  disabled = false,
  showClear = false,
  boldItems = false,
  chips,
  grouped,
  options,
  multiselectLimit,
  panelHeaderTemplate,
  preserveSelectedOnFilterSelectAll = true,
  error = false,
  optionLabel,
  optionGroupLabel,
  optionGroupChildren,
  size = DropdownSizes.medium,
  loading = false,
  value,
  name,
  onChange,
  ...props
}: DropdownProps) => {
  const disabledStyle = disabled ? styles.dropdownDisabled : null;
  const boldStyle = boldItems ? 'p-dropdown-bold' : undefined;
  const errorStyle = error ? 'p-invalid' : undefined;
  const finalClassName = `${boldStyle} ${errorStyle} ${size}`;

  const dropdownValues = toDropdownValues(value);
  const sanitizedValue = Array.isArray(value)
    ? dropdownValues.filter(
        (dropdownValue) =>
          dropdownValue !== '' && options.some((option) => getOptionValue(option) === dropdownValue)
      )
    : value;

  const finalProps = {
    disabled: disabled || loading,
    showClear: showClear,
    className: finalClassName,
    options: options,
    value: sanitizedValue,
    loading: loading,
    dataKey: 'value',
    name: name,
    onChange: onChange,
    ...props
  };

  const labelProps = {
    className: `${disabledStyle} fw-bold`
  };

  const preservingPanelHeaderTemplate = (headerOptions: MultiSelectPanelHeaderTemplateOptions) => {
    const visibleValues = getVisibleOptionValues(options, headerOptions.props.filterValue);
    const { allVisibleValuesSelected, nextSelectedValues } = getToggledSelection(
      dropdownValues,
      visibleValues
    );
    const onToggleAll = (event: PanelHeaderCheckboxChangeEvent) => {
      if (onChange) {
        onChange({
          originalEvent: event.originalEvent,
          value: nextSelectedValues,
          target: {
            name: name || '',
            id: '',
            value: nextSelectedValues
          }
        });
      }
    };
    const checkboxElement = cloneElement(headerOptions.checkboxElement, {
      checked: allVisibleValuesSelected,
      onChange: onToggleAll
    });
    const preservingOptions = {
      ...headerOptions,
      checkboxElement,
      checked: allVisibleValuesSelected,
      onChange: onToggleAll
    };

    if (typeof panelHeaderTemplate === 'function') {
      return panelHeaderTemplate(preservingOptions);
    }

    if (panelHeaderTemplate) {
      return panelHeaderTemplate;
    }

    return (
      <div className={headerOptions.className}>
        {checkboxElement}
        {headerOptions.filterElement}
        {headerOptions.closeElement}
      </div>
    );
  };

  const finalPanelHeaderTemplate = preserveSelectedOnFilterSelectAll
    ? preservingPanelHeaderTemplate
    : panelHeaderTemplate;

  return (
    <>
      <label {...labelProps}>{label}</label>
      {chips ? (
        <MultiSelect
          {...finalProps}
          filter
          display="chip"
          selectionLimit={multiselectLimit}
          panelHeaderTemplate={finalPanelHeaderTemplate}
        />
      ) : grouped ? (
        <MultiSelect
          {...finalProps}
          optionLabel={optionLabel}
          optionGroupLabel={optionGroupLabel}
          optionGroupChildren={optionGroupChildren}
        />
      ) : (
        <PrimeDropdown {...finalProps} filter />
      )}
    </>
  );
};

/**
 * SelectItem API as defined by PrimeReact
 * (https://www.primefaces.org/primereact/showcase/#/dropdown)
 */
export type SelectItem = {
  label: string;
  value?: string | number; // added the number with an OR operator
  className?: string;
  title?: string;
  disabled?: boolean;
};

/**
 * This is PrimeReact's event object for onChange, taken from source
 * (primereact/components/dropdown/Dropdown.d.ts)
 */
export type DropdownChangeEvent = Parameters<
  NonNullable<ComponentProps<typeof MultiSelect>['onChange']>
>[0];

export interface DropdownProps {
  /** Placeholder when no value is selected */
  placeholder?: string;
  /**
   * Array of items to display in the list.
   * It can be an array of strings.
   * It can also be [SelectItem](https://www.primefaces.org/primereact/showcase/#/dropdown) instances
   * (see exported type `SelectItem`),
   * which basically means each item should be of the format `{label: 'sample label', value: 'sample value'}`.
   */
  options: Array<Partial<SelectItem> | string>;
  /** Disable selecting from this list */
  disabled?: boolean;
  /** Shows a loading spinner and disables the component */
  loading?: boolean;
  /** Label to be shown alongside the list */
  label?: string;
  /** Whether to show an icon to clear current selection */
  showClear?: boolean;
  /** Name is needed when working with forms */
  name?: string;
  /** Sets the currently selected item */
  value?: DropdownSelectionValue;
  /** onChange is needed when working with forms */
  onChange?: (e: DropdownChangeEvent) => void;
  /** boldItems is needed when working with forms */
  boldItems?: boolean;
  /** chips is needed when working with ChipDropdown */
  chips?: boolean;
  /** Number of selections allowed in multi select */
  multiselectLimit?: number;
  /** Option to override the panel header (select all button, etc) in MultiSelect */
  panelHeaderTemplate?: ReactNode | ((options: MultiSelectPanelHeaderTemplateOptions) => ReactNode);
  /** Keep hidden selections when selecting or clearing filtered options in MultiSelect. Defaults to true. */
  preserveSelectedOnFilterSelectAll?: boolean;
  /** Whether this dropdown contains an error. Applies relevant styles */
  error?: boolean;
  /** Whether this dropdown is a grouped dropdown */
  grouped?: boolean;
  /** Label of the child element */
  optionLabel?: string;
  /** Label of the parent element */
  optionGroupLabel?: string;
  /** Children array name */
  optionGroupChildren?: string;
  /** size of the dropdown*/
  size?: DropdownSizes;
}

export default Dropdown;
